/* istanbul ignore file */
import {
  selectProductByKey,
  CARD_PRODUCT,
  PERSONAL_CHECKING_ACCOUNT,
  PERSONAL_SAVINGS_ACCOUNT,
  loadProductsRegistry,
  loadProducts,
  getProductRegistryErrors,
  getProductsRegistry,
  getProductRegistryById,
  getSelectedProduct,
  selectProduct,
} from '@americanexpress/product-ducks';
import {
  getIsCardProduct,
  getIsPersonalCheckingProduct,
  getIsPersonalSavingsProduct,
  // getBankingDomain,
} from '@americanexpress/rerouter';
import redirect from './redirect';
import {
  getIsValidRouteForType,
  getIsValidCardRoute,
  getIsValidBankingRoute,
  getIsValidSharedRoute,
  getIsHandledProduct,
} from './routeUtils';
import { productTypeRoutes } from './productTypeRoutes';

const shouldChangeToCard = (
  selectedProductRegistry,
  pathName,
  firstCardId
) => !getIsCardProduct(selectedProductRegistry) && getIsValidCardRoute(pathName)
  && firstCardId;

const shouldChangeToPersonalChecking = (
  selectedProductRegistry,
  pathName,
  firstPersonalCheckingId
) => !getIsPersonalCheckingProduct(selectedProductRegistry) && getIsValidBankingRoute(pathName)
  && firstPersonalCheckingId;

const selectProductRedirect = (store) => async (
  nextState,
  replace,
  callback
// eslint-disable-next-line complexity -- TODO: 22/20 limit. Refactor to reduce complexity.
) => {
  const { dispatch, getState } = store;
  const pathName = nextState.location.pathname;
  const accountKeyFromQuery = nextState.location.query.account_key;
  const opaqueAccountIdFromQuery = nextState.location.query.opaqueAccountId;
  const cardRoute = productTypeRoutes.get(CARD_PRODUCT);
  const bankRoute = productTypeRoutes.get(PERSONAL_CHECKING_ACCOUNT);
  const savingsRoute = productTypeRoutes.get(PERSONAL_SAVINGS_ACCOUNT);

  // attempt to load registry to at least have a populated "empty" registry
  // @TODO: Remove this and test within requireAuth. Need to determine if any
  // shared routes require the use of account_key.
  try {
    await dispatch(loadProductsRegistry());
    await dispatch(loadProducts({ types: [CARD_PRODUCT] }));
  } catch (err) {
    /* do nothing */
  }

  const state = getState();
  const selectedProduct = getSelectedProduct(state);

  const {
    cardError,
    customerError,
  } = getProductRegistryErrors(state);

  // exit early on errors from registry, dont proceed to try to select
  if (cardError) {
    return callback();
  }

  if (!selectedProduct && customerError) {
    return callback();
  }

  const selectedProductRegistry = getProductRegistryById(state, selectedProduct) || {};
  const selectedProductType = selectedProductRegistry.type;
  // const bankingDomain = getBankingDomain(state);

  // customer is entering in the context of a card
  if (accountKeyFromQuery) {
    await dispatch(selectProductByKey(accountKeyFromQuery));
    // course correct to dashboard if trying to reach invalid card route
    if (!getIsValidCardRoute(pathName)) {
      redirect({
        destination: `${cardRoute}?account_key=${accountKeyFromQuery}`,
        replace,
        callback,
        forceRefresh: true,
      });
    }
    return callback();
  }

  // customer is entering in the context of a banking product
  if (opaqueAccountIdFromQuery) {
    await dispatch(selectProduct(opaqueAccountIdFromQuery));
    // course correct to banking/dashboard if trying to reach invalid banking route
    if (
      !getIsValidBankingRoute(pathName) && getIsPersonalCheckingProduct(selectedProductRegistry)
    ) {
      redirect({
        destination: `${bankRoute}?opaqueAccountId=${opaqueAccountIdFromQuery}`,
        replace,
        callback,
        forceRefresh: true,
      });
      // course correct to savings dashboard if trying to reach invalid savings route
    } else if (
      !getIsValidSharedRoute(pathName) && getIsPersonalSavingsProduct(selectedProductRegistry)
    ) {
      redirect({
        destination: `${savingsRoute}?opaqueAccountId=${opaqueAccountIdFromQuery}`,
        replace,
        callback,
        forceRefresh: true,
      });
    }
    return callback();
  }

  if (selectedProductType && getIsHandledProduct(selectedProductType)) {
    const defaultRoute = productTypeRoutes.get(selectedProductType);

    // customer is not entering with any specific product
    // course correct on desired route and what products in registry
    const {
      [CARD_PRODUCT]: cardRegistry,
      [PERSONAL_CHECKING_ACCOUNT]: personalCheckingRegistry,
    } = getProductsRegistry(state);

    const [{ accountToken: firstCardId } = {}] = cardRegistry;
    const { opaqueAccountId: firstPersonalCheckingId } = personalCheckingRegistry.find(
      ({ status }) => status === 'ACTIVE') || personalCheckingRegistry[0] || {};

    // if the product is not a card, but they are trying to enter a card route and have a card
    if (!getIsValidRouteForType(selectedProductType, pathName)) {
      if (shouldChangeToCard(selectedProductRegistry, pathName, firstCardId)) {
        return redirect({
          destination: `${pathName}?account_key=${firstCardId}`,
          replace,
          callback,
          forceRefresh: true,
        });
      }

      if (shouldChangeToPersonalChecking(
        selectedProductRegistry, pathName, firstPersonalCheckingId
      )) {
        // only change to the first available personal checking product and keep the intended route
        return redirect({
          destination: `${pathName}?opaqueAccountId=${firstPersonalCheckingId}`,
          replace,
          callback,
          forceRefresh: true,
        });
      }

      // should redirect for savings
      if (selectedProductType === PERSONAL_SAVINGS_ACCOUNT) {
        // const { opaqueAccountId: firstPersonalSavingsId } = personalSavingsRegistry.find(
        //   ({ status }) => status === 'ACTIVE') || personalSavingsRegistry[0] || {};
        return redirect({
          destination: `${savingsRoute}`, replace, callback, forceRefresh: true,
        });
      }

      // redirect to the default route of the currently selected product
      return redirect({ destination: defaultRoute, replace, callback });
    }
  }

  // fall through to requireAuth
  return callback();
};

export default selectProductRedirect;
